// The app's frame (docs/08-app-design.md sections 3 and 4) in the public
// site's identity (website build brief section 9; round-4 brief section 6):
// a quiet top bar with the wordmark, the network and the lock control; on
// wide screens a labelled rail of destinations beside the screen; on phones
// a bottom bar of Overview, Swap, Deposit, Send and More, where More opens a
// small list of the remaining destinations (Stake, Protection, Verify, Settings).
// Lock stays in the top bar on every width.
// The entrance (/welcome, /locked, /create, /restore) does not use this
// frame: it wears the site's own (EntranceFrame, CeremonyFrame;
// app/art/welcome/PLAN.md section 4).

import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { DOCS_URL, SOURCE_URL } from '../config';
import { networkLabel } from '../network';
import { session, useSession } from '../session';

export { DOCS_URL, SOURCE_URL };

type Icon = 'overview' | 'swap' | 'deposit' | 'send' | 'stake' | 'protection' | 'settings' | 'verify' | 'more';

function NavIcon({ name }: { name: Icon }) {
  const paths: Record<Icon, ReactNode> = {
    overview: <path d="M3 4.5h14M3 10h14M3 15.5h9" />,
    swap: <path d="M4 7h11l-3-3M16 13H5l3 3" />,
    deposit: <path d="M10 3v10M6 9l4 4 4-4M4 17h12" />,
    send: <path d="M10 17V7M6 11l4-4 4 4M4 3h12" />,
    stake: <path d="M4 16h12M6 16V9M10 16V5M14 16v-4" />,
    protection: <path d="M10 2.8 16 5v5c0 3.6-2.6 6.2-6 7.2-3.4-1-6-3.6-6-7.2V5l6-2.2Z" />,
    settings: <path d="M3 6h8M14 6h3M3 14h3M9 14h8M11 4v4M6 12v4" />,
    verify: <path d="m4 10.5 3.5 3.5L16 5.5" />,
    more: <path d="M4.5 10h.01M10 10h.01M15.5 10h.01" strokeWidth="2.6" />,
  };
  return (
    <svg className="nav-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        {paths[name]}
      </g>
    </svg>
  );
}

const DESTINATIONS: Array<{ href: string; label: string; icon: Icon; group: 'vault' | 'trust' | 'utility' }> = [
  { href: '/vault', label: 'Overview', icon: 'overview', group: 'vault' },
  { href: '/swap', label: 'Swap', icon: 'swap', group: 'vault' },
  { href: '/deposit', label: 'Deposit', icon: 'deposit', group: 'vault' },
  { href: '/withdraw', label: 'Send', icon: 'send', group: 'vault' },
  { href: '/stake', label: 'Stake', icon: 'stake', group: 'vault' },
  { href: '/keys', label: 'Protection', icon: 'protection', group: 'trust' },
  { href: '/verify', label: 'Verify', icon: 'verify', group: 'trust' },
  { href: '/settings', label: 'Settings', icon: 'settings', group: 'utility' },
];

/** The dashboard's theme (owner, 12 Oct): dark by default; the choice is remembered on this device. */
const THEME_KEY = 'zq-app-theme';
function useAppTheme(): ['dark' | 'light', () => void] {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    try {
      return localStorage.getItem(THEME_KEY) === 'light' ? 'light' : 'dark';
    } catch {
      return 'dark';
    }
  });
  const toggle = () =>
    setTheme((t) => {
      const next = t === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem(THEME_KEY, next);
      } catch {
        // storage blocked: the choice lasts for this screen
      }
      return next;
    });
  return [theme, toggle];
}

/** The phone bar: the four everyday tasks, then More. */
const TABS = ['/vault', '/swap', '/deposit', '/withdraw'];
/** Behind More on phones: the trust and utility destinations (no Activity screen exists yet, so none is listed). */
const MORE = ['/stake', '/keys', '/verify', '/settings'];

/**
 * The phone bar's More: a disclosure button and a short list of links above
 * the bar. It closes on Escape (focus returns to More), on a pointer or focus
 * outside it, and on any route change; choosing the page already shown closes
 * it with focus back on More, since no route change moves focus then. While
 * one of its pages is shown, More says so (aria-current), as the tabs do.
 */
function MoreMenu({ location }: { location: string }) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => setOpen(false), [location]);
  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector<HTMLElement>('a')?.focus();
    const inside = (t: EventTarget | null) => t instanceof Node && (!!panel.current?.contains(t) || !!button.current?.contains(t));
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      button.current?.focus();
    };
    const onPointer = (e: PointerEvent) => {
      if (!inside(e.target)) setOpen(false);
    };
    const onFocus = (e: FocusEvent) => {
      if (!inside(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('focusin', onFocus);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('focusin', onFocus);
    };
  }, [open]);
  const here = MORE.includes(location);
  return (
    <div className="tabbar-more">
      <button ref={button} type="button" className={here ? 'selected' : undefined} aria-current={here ? 'true' : undefined} aria-expanded={open} aria-controls={id} onClick={() => setOpen((v) => !v)}>
        <NavIcon name="more" />
        <span>More</span>
      </button>
      <div ref={panel} id={id} className="tabbar-sheet" hidden={!open}>
        <ul>
          {MORE.map((href) => {
            const d = DESTINATIONS.find((x) => x.href === href)!;
            return (
              <li key={href}>
                <Link
                  href={href}
                  className={location === href ? 'selected' : ''}
                  aria-current={location === href ? 'page' : undefined}
                  onClick={() => {
                    setOpen(false);
                    if (location === href) button.current?.focus();
                  }}
                >
                  <NavIcon name={d.icon} />
                  <span>{d.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/**
 * The screen's band above its content: its name, and a detail on the right
 * (the key tree, the step). Not a heading: each screen's own h1 is the first
 * heading, so the outline starts there. The detail is the last span inside
 * `.section-label`; the end-to-end tests read it there, so it stays a single
 * plain span.
 */
export function SectionLabel({ number, title, detail }: { number: string; title: string; detail: ReactNode }) {
  return (
    <div className="section-label">
      <p className="section-title">
        <span className="square" aria-hidden="true" data-index={number} />
        {title}
      </p>
      <span>{detail}</span>
    </div>
  );
}

export function Shell({
  number,
  title,
  detail,
  sidebar = true,
  children,
}: {
  number: string;
  title: string;
  detail: ReactNode;
  sidebar?: boolean;
  children: ReactNode;
}) {
  const snap = useSession();
  const [location, navigate] = useLocation();
  const unlocked = snap.state === 'unlocked';
  const stateWord = unlocked ? 'Unlocked' : snap.state === 'locked' ? 'Locked' : 'No vault on this device';
  const current = (href: string) => (location === href ? 'page' : undefined);
  const rail = sidebar;
  const [theme, toggleTheme] = useAppTheme();
  return (
    <div className={`site-frame app-frame ${rail ? 'has-rail' : 'no-rail'} top-nav-frame`} data-theme={theme}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="header">
        <Link href="/" className="brand" aria-label="ZeroQube home">
          <span className="wordmark" aria-hidden="true">
            ZeroQube
          </span>
        </Link>
        {rail ? (
          <nav className="top-nav" aria-label="Screens">
            {DESTINATIONS.map((d) => (
              <Link key={d.href} href={d.href} className={location === d.href ? 'selected' : ''} aria-current={current(d.href)}>
                {d.label}
              </Link>
            ))}
          </nav>
        ) : null}
        <div className="header-right">
          {unlocked ? (
            // one calm control (owner, 12 Oct): the network and the unlocked state, and pressing it locks
            <button type="button" className="net-pill lock-pill" data-unlocked="true" title="Lock this vault on this device" onClick={() => {
                // the lock is published at once (session.ts, R68); the door opens now, not after the teardown, so a
                // slow teardown never pulls the user back to /locked from wherever they went meanwhile
                void session.lock();
                navigate('/locked');
              }}>
              <i className="status-dot" aria-hidden="true" />
              <span>{networkLabel(snap.settings.rpcUrl)}</span>
              <span className="net-state">{stateWord}</span>
              <span className="lock-pill-action">Lock</span>
            </button>
          ) : (
            <span className="net-pill" data-unlocked="false">
              <i className="status-dot" aria-hidden="true" />
              <span>{networkLabel(snap.settings.rpcUrl)}</span>
              <span className="net-state">{stateWord}</span>
            </span>
          )}
          <button type="button" className="theme-toggle" onClick={toggleTheme} aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
              <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
              <path d="M8 1.75a6.25 6.25 0 0 1 0 12.5Z" fill="currentColor" />
            </svg>
          </button>
        </div>
      </header>
      {snap.development ? (
        <div className="dev-banner" role="status">
          DEVELOPMENT SESSION · the program check is off until you lock
        </div>
      ) : null}
      <div className={`console-layout ${rail ? '' : 'single'}`.trim()}>
        <main className="console-content" id="main" tabIndex={-1}>
          <SectionLabel number={number} title={title} detail={detail} />
          {children}
        </main>
      </div>
      {rail ? (
        <nav className="tabbar" aria-label="Main">
          {TABS.map((href) => {
            const d = DESTINATIONS.find((x) => x.href === href)!;
            return (
              <Link key={href} href={href} className={location === href ? 'selected' : ''} aria-current={current(href)}>
                <NavIcon name={d.icon} />
                <span>{d.label}</span>
              </Link>
            );
          })}
          <MoreMenu location={location} />
        </nav>
      ) : null}
      <footer className="console-footer">
        <span className="console-footer-status">
          <span className="status-chip">Experimental · Unaudited</span>
          <span>Spending authority: hash-based, one-time keys</span>
        </span>
        <span className="console-footer-links">
          <Link href="/protection">Docs</Link>
        </span>
      </footer>
    </div>
  );
}
